import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongCall, addLongPut, addShortCall, addShortPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const inverseIronButterfly: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // ATM options
  const atmCallStrike = findClosestStrikePrice(strikesAtExpiration, spot);
  const atmPutStrike = atmCallStrike; // Assuming the closest strike to spot is the same for both call and put

  // OTM options
  const otmPutStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  );
  const otmCallStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 2 }),
  );

  return {
    optionPositions: [
      // Buy an ATM call
      addLongCall({
        strike: atmCallStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[atmCallStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[atmCallStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Buy an ATM put
      addLongPut({
        strike: atmPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[atmPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[atmPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell an OTM call
      addShortCall({
        strike: otmCallStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[otmCallStrike]?.c?.a || 0,
          bid: strikesAtExpiration?.c?.[otmCallStrike]?.c?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell an OTM put
      addShortPut({
        strike: otmPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[otmPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[otmPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
    ],
    stockPositions: [],
  };
};
